import { helpers } from '@vuelidate/validators';
import type { PolicyDocumentStatusRow, RuleViolatingPolicy } from '@/services/v2/ClientReadinessService';

// Shared issue predicates for the Data Readiness Wizard. Steps 1-5 and the
// Step 6 review summary must agree on what counts as an outstanding issue, so
// the definitions live here rather than in the individual step components.

// Domain-format validators shared between Step 4's vuelidate rules and the
// readiness counts. Empty values pass — "domain missing" is a separate concern
// handled by countDemographicsIssues.
export const validDomain = (url: string) => {
  return !helpers.req(url) || /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,}){1}$/.test(url);
};

export const noWWW = (url: string) => {
  return !helpers.req(url) || !/^www\..*$/.test(url);
};

export function isDomainFormatInvalid(domain: string): boolean {
  return !validDomain(domain) || !noWWW(domain) || domain.length > 100;
}

export function isDocumentIssue(row: PolicyDocumentStatusRow): boolean {
  return row.status === 'missing' || row.status === 'unpublished';
}

export function countDocumentIssues(rows: PolicyDocumentStatusRow[]): number {
  return rows.filter(isDocumentIssue).length;
}

// Matches Step 3's displayed total: a policy violating multiple Epic rules
// counts once per rule.
export function countEpicIssues(policies: RuleViolatingPolicy[]): number {
  return (
    policies.filter((p) => p.invalidPolicyNumber).length +
    policies.filter((p) => p.invalidMissingPremiumOrFees).length +
    policies.filter((p) => p.invalidDates).length
  );
}

export function hasPolicyRuleViolation(p: RuleViolatingPolicy): boolean {
  return Boolean(p.noLimitRule) || p.invalidRetentionRule || p.invalidAttachmentPointRule;
}

export function countPolicyDetailIssues(policies: RuleViolatingPolicy[]): number {
  return policies.filter(hasPolicyRuleViolation).length;
}

// Readiness rule for Client Demographics: unless the account is flagged as
// having no domain, a missing or malformed domain name is one outstanding issue.
export function countDemographicsIssues(domainName: string | null | undefined, noDomain: boolean): number {
  if (noDomain) return 0;
  if (!domainName) return 1;
  return isDomainFormatInvalid(domainName) ? 1 : 0;
}
